/**
 * Modern Predictive Search
 * Enhanced predictive search functionality for the modern header
 */

class ModernPredictiveSearch {
  constructor() {
    this.searchForm = document.querySelector('.modern-header__search-form');
    this.searchInput = document.querySelector('.modern-header__search-input');
    this.searchContainer = document.querySelector('.modern-header__search');
    
    if (!this.searchForm || !this.searchInput) {
      return;
    }
    
    this.resultsContainer = null;
    this.searchTimeout = null;
    this.isLoading = false;
    this.currentQuery = '';
    this.isMouseOverResults = false;
    
    this.init();
  }
  
  init() {
    this.createResultsContainer();
    this.attachEventListeners();
    this.preloadPredictiveSearchSection();
    // Ensure results are hidden initially
    this.hideResults();
  }
  
  createResultsContainer() {
    // Check if container already exists
    this.resultsContainer = this.searchForm.querySelector('.modern-predictive-search__results');
    
    if (!this.resultsContainer) {
      // Create predictive search results container
      this.resultsContainer = document.createElement('div');
      this.resultsContainer.className = 'modern-predictive-search__results';
      this.resultsContainer.innerHTML = `
        <div class="modern-predictive-search__loading">
          <div class="modern-predictive-search__spinner"></div>
        </div>
        <div class="modern-predictive-search__content"></div>
      `;
      
      // Insert after the search form
      this.searchForm.appendChild(this.resultsContainer);
    } else {
      // Clear any existing content
      const contentContainer = this.resultsContainer.querySelector('.modern-predictive-search__content');
      if (contentContainer) {
        contentContainer.innerHTML = '';
      }
    }
  }
  
  attachEventListeners() {
    // Input events - optimized debounce for fast, smooth response
    this.searchInput.addEventListener('input', this.debounce(() => {
      this.handleSearch();
    }, 150));
    
    // Focus/blur events
    this.searchInput.addEventListener('focus', () => {
      if (this.searchInput.value.trim().length >= 2) {
        this.handleSearch();
      }
    });
    
    this.searchInput.addEventListener('blur', (e) => {
      // Delay hiding to allow clicks on results
      setTimeout(() => {
        if (!this.resultsContainer.contains(document.activeElement) && !this.isMouseOverResults) {
          this.hideResults();
        }
      }, 200);
    });
    
    // Form submit
    this.searchForm.addEventListener('submit', (e) => {
      if (this.searchInput.value.trim().length === 0) {
        e.preventDefault();
      }
      this.hideResults();
    });
    
    // Click outside - check both searchContainer and resultsContainer
    document.addEventListener('click', (e) => {
      if (!this.searchContainer.contains(e.target) && 
          !this.resultsContainer.contains(e.target)) {
        this.hideResults();
      }
    });
    
    // Track mouse over results to prevent closing
    this.resultsContainer.addEventListener('mouseenter', (e) => {
      e.stopPropagation();
      this.isMouseOverResults = true;
    });
    
    this.resultsContainer.addEventListener('mouseleave', (e) => {
      this.isMouseOverResults = false;
    });
    
    // Prevent blur on mousedown for interactive elements only
    this.resultsContainer.addEventListener('mousedown', (e) => {
      // Only prevent default for non-link elements to allow link clicks
      if (e.target.tagName !== 'A' && !e.target.closest('a')) {
        e.preventDefault();
      }
    });
    
    this.resultsContainer.addEventListener('click', (e) => {
      e.stopPropagation();
    });
    
    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.hideResults();
        this.searchInput.blur();
      }
    });
    
    // Arrow key navigation
    this.searchInput.addEventListener('keydown', (e) => {
      this.handleKeyNavigation(e);
    });
  }
  
  handleSearch() {
    const query = this.searchInput.value.trim();
    
    // Show hint if user is typing but query is too short
    if (query.length === 1) {
      this.showTypingHint();
      return;
    }
    
    // Clear results if query is empty
    if (query.length < 2) {
      this.hideResults();
      this.currentQuery = '';
      return;
    }
    
    // If query hasn't changed and results are visible, don't fetch again
    if (query === this.currentQuery && this.resultsContainer.classList.contains('active')) {
      return;
    }
    
    // Update current query and perform search
    this.currentQuery = query;
    this.performSearch(query);
  }
  
  showTypingHint() {
    const contentContainer = this.resultsContainer.querySelector('.modern-predictive-search__content');
    contentContainer.innerHTML = `
      <div class="modern-predictive-search__hint">
        <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M20 34C27.732 34 34 27.732 34 20C34 12.268 27.732 6 20 6C12.268 6 6 12.268 6 20C6 27.732 12.268 34 20 34Z" stroke="#9CA3AF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M30 30L42 42" stroke="#9CA3AF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <p>Keep typing to search...</p>
      </div>
    `;
    this.showResults();
  }
  
  async performSearch(query) {
    // Store the query this search is for
    const searchId = Date.now();
    this.currentSearchId = searchId;
    
    // Only show loading spinner if we don't have results yet
    const hasExistingResults = this.resultsContainer.querySelector('.search-results');
    if (!hasExistingResults) {
      this.showLoading();
    } else {
      // Keep existing results visible but add a subtle loading indicator
      this.resultsContainer.classList.add('updating');
    }
    
    try {
      const searchParams = new URLSearchParams({
        q: query,
        'resources[type]': 'product,article,page,collection',
        'resources[limit]': '10',
        'resources[options][unavailable_products]': 'hide',
        'resources[options][fields]': 'title,body,vendor,product_type,tag,variants.title,variants.sku',
        section_id: 'predictive-search'
      });
      
      // Try to get predictive search URL from various sources
      let predictiveSearchUrl = '/search/suggest'; // Default Shopify URL
      
      if (typeof theme !== 'undefined' && theme?.routes?.predictive_search_url) {
        predictiveSearchUrl = theme.routes.predictive_search_url;
      } else if (window.Shopify?.routes?.root) {
        // Fallback to constructing URL from Shopify root
        predictiveSearchUrl = window.Shopify.routes.root + 'search/suggest';
      }
      
      const searchUrl = `${predictiveSearchUrl}?${searchParams}`;
      const response = await fetch(searchUrl);
      
      // Check if this search is still relevant (user hasn't typed more)
      if (this.currentSearchId !== searchId) {
        return; // Discard outdated results
      }
      
      if (!response.ok) {
        throw new Error(`Search request failed: ${response.status}`);
      }
      
      const html = await response.text();
      
      // Check again if this search is still relevant
      if (this.currentSearchId !== searchId) {
        return;
      }
      
      const resultsMarkup = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector('#shopify-section-predictive-search');
      
      if (resultsMarkup) {
        this.renderResults(resultsMarkup.innerHTML);
      } else {
        this.renderNoResults();
      }
    } catch (error) {
      console.error('Predictive search error:', error);
      this.renderError();
    } finally {
      this.resultsContainer.classList.remove('updating');
    }
  }
  
  renderResults(html) {
    const contentContainer = this.resultsContainer.querySelector('.modern-predictive-search__content');
    
    // Check if we actually have results
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    const hasResults = tempDiv.querySelector('.search-results');
    
    if (!hasResults) {
      this.renderNoResults();
      return;
    }
    
    // Only fade if we have existing results (smoother updates)
    const hasExisting = contentContainer.querySelector('.search-results');
    
    if (hasExisting) {
      // Quick, smooth transition without visible flicker
      contentContainer.innerHTML = html;
      this.processResults();
      this.hideLoading();
      this.showResults();
      this.initializeKeyboardNavigation();
      contentContainer.style.opacity = '1';
    } else {
      // First time showing results
      contentContainer.innerHTML = html;
      this.processResults();
      this.hideLoading();
      this.showResults();
      this.initializeKeyboardNavigation();
      contentContainer.style.opacity = '1';
    }
  }
  
  processResults() {
    // Add modern styling classes to the results
    const categories = this.resultsContainer.querySelectorAll('.search-categories');
    categories.forEach(cat => cat.classList.add('modern-predictive-search__categories'));
    
    const results = this.resultsContainer.querySelectorAll('.search-results');
    results.forEach(result => {
      result.classList.add('modern-predictive-search__section');
      // Ensure results sections stay active
      if (result.querySelector('.predictive-search-product-card, .product-cart-item')) {
        result.classList.add('active');
      }
    });
    
    // Count total results
    const productCount = this.resultsContainer.querySelectorAll('.predictive-search-product-card').length;
    const articleCount = this.resultsContainer.querySelectorAll('#predictive-results-article .product-cart-item').length;
    const pageCount = this.resultsContainer.querySelectorAll('#predictive-results-pages .predictive-search__item').length;
    const totalCount = productCount + articleCount + pageCount;
    
    // Add result counter
    if (totalCount > 0) {
      this.addResultCounter(totalCount);
      this.addViewAllLink();
    }
    
    // Enhance product cards
    const productCards = this.resultsContainer.querySelectorAll('.predictive-search-product-card');
    productCards.forEach((card, index) => {
      // Add animation delay for staggered appearance (only for first 5 items)
      if (index < 5) {
        card.style.animationDelay = `${index * 30}ms`;
      }
      
      // Add hover states
      card.addEventListener('mouseenter', (e) => {
        e.stopPropagation();
        this.removeAllActiveStates();
        card.classList.add('active');
        
        // Keep the container focused to prevent blur
        if (document.activeElement !== this.searchInput) {
          this.searchInput.focus();
        }
      });
      
      // Prevent click from closing results and maintain focus
      card.addEventListener('click', (e) => {
        e.stopPropagation();
        // Keep search input focused
        this.searchInput.focus();
      });
    });
    
    // Handle category switching
    const categoryLinks = this.resultsContainer.querySelectorAll('.search-categories a');
    categoryLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        this.switchCategory(link);
      });
    });
  }
  
  switchCategory(link) {
    // Remove active from all categories
    const allLinks = this.resultsContainer.querySelectorAll('.search-categories a');
    allLinks.forEach(l => l.classList.remove('active'));
    
    // Add active to clicked
    link.classList.add('active');
    
    // Hide all sections
    const allSections = this.resultsContainer.querySelectorAll('.search-results');
    allSections.forEach(s => s.classList.remove('active'));
    
    // Show target section
    const target = link.getAttribute('href');
    const targetSection = this.resultsContainer.querySelector(target);
    if (targetSection) {
      targetSection.classList.add('active');
    }
  }
  
  renderNoResults() {
    const contentContainer = this.resultsContainer.querySelector('.modern-predictive-search__content');
    contentContainer.innerHTML = `
      <div class="modern-predictive-search__no-results">
        <p>No results found for "${this.currentQuery}"</p>
        <p class="modern-predictive-search__suggestion">Try checking your spelling or using more general terms</p>
      </div>
    `;
    
    this.hideLoading();
    this.showResults();
  }
  
  renderError() {
    const contentContainer = this.resultsContainer.querySelector('.modern-predictive-search__content');
    contentContainer.innerHTML = `
      <div class="modern-predictive-search__error">
        <p>Sorry, something went wrong. Please try again.</p>
      </div>
    `;
    
    this.hideLoading();
    this.showResults();
  }
  
  showResults() {
    this.resultsContainer.classList.add('active');
    this.searchContainer.classList.add('has-results');
    
    // Ensure all visible search results sections are active
    const visibleSections = this.resultsContainer.querySelectorAll('.search-results');
    visibleSections.forEach(section => {
      if (section.querySelector('.predictive-search-product-card, .product-cart-item, .predictive-search__item')) {
        section.classList.add('active');
      }
    });
    
    // Mark that we've shown results at least once (prevents re-animation)
    setTimeout(() => {
      this.resultsContainer.classList.add('has-shown-once');
    }, 300);
  }
  
  hideResults() {
    this.resultsContainer.classList.remove('active', 'has-shown-once');
    this.searchContainer.classList.remove('has-results');
    this.removeAllActiveStates();
    
    // Clear the content after animation completes
    setTimeout(() => {
      if (!this.resultsContainer.classList.contains('active')) {
        const contentContainer = this.resultsContainer.querySelector('.modern-predictive-search__content');
        if (contentContainer) {
          contentContainer.innerHTML = '';
        }
      }
    }, 300);
  }
  
  showLoading() {
    this.isLoading = true;
    this.resultsContainer.classList.add('loading');
    this.showResults();
  }
  
  hideLoading() {
    this.isLoading = false;
    this.resultsContainer.classList.remove('loading');
  }
  
  // Keyboard navigation
  handleKeyNavigation(e) {
    if (!this.resultsContainer.classList.contains('active')) return;
    
    const items = this.resultsContainer.querySelectorAll('.predictive-search-product-card, .predictive-search__item');
    const activeItem = this.resultsContainer.querySelector('.active');
    let currentIndex = Array.from(items).indexOf(activeItem);
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        currentIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0;
        this.setActiveItem(items[currentIndex]);
        break;
        
      case 'ArrowUp':
        e.preventDefault();
        currentIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1;
        this.setActiveItem(items[currentIndex]);
        break;
        
      case 'Enter':
        if (activeItem) {
          e.preventDefault();
          // Check if the item itself is a link
          if (activeItem.tagName === 'A') {
            activeItem.click();
          } else {
            const link = activeItem.querySelector('a');
            if (link) link.click();
          }
        }
        break;
    }
  }
  
  setActiveItem(item) {
    this.removeAllActiveStates();
    if (item) {
      item.classList.add('active');
      item.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }
  
  removeAllActiveStates() {
    // Only remove active state from product cards and navigation items, not containers
    const activeItems = this.resultsContainer.querySelectorAll('.predictive-search-product-card.active, .predictive-search__item.active, .search-categories a.active');
    activeItems.forEach(item => {
      item.classList.remove('active');
    });
  }
  
  initializeKeyboardNavigation() {
    const items = this.resultsContainer.querySelectorAll('.predictive-search-product-card, .predictive-search__item');
    items.forEach((item, index) => {
      item.setAttribute('data-index', index);
    });
  }
  
  addResultCounter(count) {
    // Remove existing counter if any
    const existingCounter = this.resultsContainer.querySelector('.predictive-search-counter');
    if (existingCounter) existingCounter.remove();
    
    // Add new counter
    const counter = document.createElement('div');
    counter.className = 'predictive-search-counter';
    counter.innerHTML = `<span>${count} result${count !== 1 ? 's' : ''} for "${this.currentQuery}"</span>`;
    
    // Insert at the top of content
    const content = this.resultsContainer.querySelector('.modern-predictive-search__content');
    content.insertBefore(counter, content.firstChild);
  }
  
  addViewAllLink() {
    // Remove existing link if any
    const existingLink = this.resultsContainer.querySelector('.predictive-search-view-all');
    if (existingLink) existingLink.remove();
    
    // Add view all link
    const viewAllLink = document.createElement('div');
    viewAllLink.className = 'predictive-search-view-all';
    viewAllLink.innerHTML = `
      <a href="${theme?.routes?.search_url || '/search'}?q=${encodeURIComponent(this.currentQuery)}">
        View all results
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M6 3L11 8L6 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </a>
    `;
    
    // Append outside of scrollable content, directly to results container
    this.resultsContainer.appendChild(viewAllLink);
  }
  
  // Utility function for debouncing
  debounce(func, wait) {
    return (...args) => {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => func.apply(this, args), wait);
    };
  }
  
  // Preload predictive search section for faster results
  preloadPredictiveSearchSection() {
    // Get predictive search URL using same logic as performSearch
    let predictiveSearchUrl = '/search/suggest';
    
    if (typeof theme !== 'undefined' && theme?.routes?.predictive_search_url) {
      predictiveSearchUrl = theme.routes.predictive_search_url;
    } else if (window.Shopify?.routes?.root) {
      predictiveSearchUrl = window.Shopify.routes.root + 'search/suggest';
    }
    
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = `${predictiveSearchUrl}?q=test&section_id=predictive-search`;
    document.head.appendChild(link);
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    // Small delay to ensure all scripts are loaded
    setTimeout(() => {
      window.modernPredictiveSearch = new ModernPredictiveSearch();
    }, 50);
  });
} else {
  // DOM already loaded - still add small delay for theme object
  setTimeout(() => {
    window.modernPredictiveSearch = new ModernPredictiveSearch();
  }, 50);
}

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = ModernPredictiveSearch;
}
